import { createElement } from 'react';
import { LineChart } from '@mui/x-charts/LineChart';
import { Box, Typography, Alert } from '@mui/material';
import { Link } from 'react-router-dom';
import TimelineIcon from '@mui/icons-material/Timeline';
import { useTranslate } from 'react-admin';
import { appIcons } from './common';
import { ActiveUsersHistoryItem, Handler, Filter } from './types';

export const APP_META = [
    { name: 'opepalvelu', label: 'Opettajat', color: '#1976d2' },
    { name: 'opiskelija', label: 'Opiskelijat', color: '#2e7d32' },
    { name: 'oppilas', label: 'Oppilaat', color: '#ed6c02' },
];

export const pivotHistory = (
    data: ActiveUsersHistoryItem[] | null,
    { total = false }: { total?: boolean } = {}
) => {
    const rows = data || [];
    const dates = Array.from(new Set(rows.map(d => d.date))).sort();
    const byKey = new Map<string, number>();
    rows.forEach(d => byKey.set(`${d.date}|${d.app}`, d.count));

    const series: {
        label: string;
        color: string;
        data: number[];
        showMark?: boolean;
    }[] = APP_META.map(m => ({
        label: m.label,
        color: m.color,
        showMark: false,
        data: dates.map(dt => byKey.get(`${dt}|${m.name}`) ?? 0),
    }));

    if (total) {
        series.push({
            label: 'Yhteensä',
            color: '#616161',
            showMark: false,
            data: dates.map((_, i) =>
                series.reduce((s, ser) => s + (ser.data[i] || 0), 0)
            ),
        });
    }

    return { dates, series };
};

const ActiveUsersHistory = (
    props: { data: ActiveUsersHistoryItem[] | null } & Handler & Filter
) => {
    const translate = useTranslate();
    const { data, handler } = props;
    const { dates, series } = pivotHistory(data);

    const title = translate('statistics.activeusershistory.title');
    const tooltip = translate('statistics.activeusershistory.tooltip');

    const chart =
        !data || data.length === 0 ? (
            <Alert severity="warning" sx={{ padding: 6 }}>
                {translate('statistics.nodata')}
            </Alert>
        ) : (
            <LineChart
                height={240}
                xAxis={[{ data: dates, scaleType: 'point' }]}
                series={series}
            />
        );

    return (
        <>
            {createElement(handler, {
                title,
                subtitle: '',
                tooltip,
                icon: appIcons.all,
                children: (
                    <>
                        {chart}
                        <Box sx={{ p: 1, textAlign: 'right' }}>
                            <Link to="/activeusers">
                                <Typography
                                    variant="body2"
                                    color="primary"
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                    }}
                                >
                                    <TimelineIcon fontSize="small" />
                                    {translate(
                                        'statistics.activeusershistory.show_page'
                                    )}
                                </Typography>
                            </Link>
                        </Box>
                    </>
                ),
            })}
        </>
    );
};

export default ActiveUsersHistory;
